import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../auth-context'
import './Sidebar.css'

const SvgIcon = ({ d, size = 18, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}>
    <path d={d} />
  </svg>
)

const icons = {
  dashboard: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  create: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  analytics: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M18 20V10M12 20V4M6 20v-6" />
    </svg>
  ),
  organization: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  alerts: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  org: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  chevron: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  collapse: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  expand: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  logout: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  menu: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  close: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
}

function OrganizationSelector() {
  const { organizations, currentOrg, setCurrentOrg } = useAuth()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event) => {
      if (!event.target.closest('.sidebar-org-selector')) setIsOpen(false)
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [isOpen])

  if (organizations.length === 0) return null

  return (
    <div className="sidebar-org-selector">
      <button className="sidebar-org-btn" onClick={() => setIsOpen(!isOpen)}>
        <span className="sidebar-org-icon">{icons.org}</span>
        <span className="sidebar-org-name">
          {currentOrg ? currentOrg.name : 'Select Organization'}
        </span>
        <span className={`sidebar-org-arrow ${isOpen ? 'open' : ''}`}>{icons.chevron}</span>
      </button>
      {isOpen && (
        <div className="sidebar-org-dropdown">
          {organizations.map((org) => (
            <button
              key={org.id}
              className={`sidebar-org-option ${currentOrg?.id === org.id ? 'active' : ''}`}
              onClick={() => {
                setCurrentOrg(org)
                setIsOpen(false)
              }}>
              <div className="sidebar-org-option-name">{org.name}</div>
              <div className="sidebar-org-option-role">{org.role_code}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Sidebar() {
  const { user, userRole, currentOrg, signOut } = useAuth()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const normalizedRole =
    userRole === 'viewer' ? 'field_agent' : userRole === 'survey_designer' ? 'designer' : userRole
  const isFieldAgent = normalizedRole === 'field_agent'

  // Show nav links for any non-field-agent user. The backend enforces
  // real permissions, so it's safe to show these links even if the
  // role_code field hasn't resolved yet from the API.
  const canManageOrg = !isFieldAgent && !!currentOrg
  const canCreate = !isFieldAgent
  const canViewAlerts = !isFieldAgent

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/')

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  return (
    <>
      <button
        className="sidebar-mobile-toggle"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle menu">
        {mobileOpen ? icons.close : icons.menu}
      </button>

      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      <aside
        className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${mobileOpen ? 'sidebar--mobile-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-logo">
            <span className="sidebar-logo-icon">S</span>
            {!collapsed && <span className="sidebar-logo-text">SurveyTip</span>}
          </Link>
          <button
            className="sidebar-collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? icons.expand : icons.collapse}
          </button>
        </div>

        {!collapsed && <OrganizationSelector />}

        <nav className="sidebar-nav">
          <div className="sidebar-nav-section-label">{!collapsed && 'Main'}</div>

          <Link
            to="/dashboard"
            className={`sidebar-nav-item ${isActive('/dashboard') ? 'active' : ''}`}
            title="Overview">
            <span className="sidebar-nav-icon">{icons.dashboard}</span>
            {!collapsed && <span className="sidebar-nav-label">Overview</span>}
          </Link>

          <Link
            to="/analytics"
            className={`sidebar-nav-item ${isActive('/analytics') ? 'active' : ''}`}
            title="Analytics">
            <span className="sidebar-nav-icon">{icons.analytics}</span>
            {!collapsed && <span className="sidebar-nav-label">Analytics</span>}
          </Link>

          {canCreate && (
            <Link
              to="/surveybuilder"
              className={`sidebar-nav-item ${isActive('/surveybuilder') ? 'active' : ''}`}
              title="Create Survey">
              <span className="sidebar-nav-icon">{icons.create}</span>
              {!collapsed && <span className="sidebar-nav-label">Create Survey</span>}
            </Link>
          )}

          {(canManageOrg || canViewAlerts) && (
            <div className="sidebar-nav-section-label">{!collapsed && 'Management'}</div>
          )}

          {canManageOrg && (
            <Link
              to="/organization"
              className={`sidebar-nav-item ${isActive('/organization') ? 'active' : ''}`}
              title="Organization">
              <span className="sidebar-nav-icon">{icons.organization}</span>
              {!collapsed && <span className="sidebar-nav-label">Organization</span>}
            </Link>
          )}

          {canViewAlerts && (
            <Link
              to="/alerts"
              className={`sidebar-nav-item ${isActive('/alerts') ? 'active' : ''}`}
              title="Alerts">
              <span className="sidebar-nav-icon">{icons.alerts}</span>
              {!collapsed && <span className="sidebar-nav-label">Alerts</span>}
            </Link>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">
              {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="sidebar-user-info">
                <div className="sidebar-user-name">
                  {user?.full_name || user?.email?.split('@')[0] || 'User'}
                </div>
                {normalizedRole && <div className="sidebar-user-role">{normalizedRole}</div>}
              </div>
            )}
          </div>
          <button onClick={signOut} className="sidebar-logout" title="Logout">
            {collapsed ? (
              icons.logout
            ) : (
              <>
                {icons.logout} <span>Logout</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  )
}
